import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, ChevronDown, ShoppingBag } from "lucide-react";
import { TopBar } from "./TopBar";
import { getCategories, getCategory } from "../lib/api";
import { useCart } from "../lib/cart";
import { LiveSearch } from "./LiveSearch";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fsSubs, setFsSubs] = useState([]);
  const megaRef = useRef(null);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    getCategory("food-service").then((d) => setFsSubs(d.subcategories)).catch(() => {});
  }, []);

  const navLink = "text-sm font-semibold text-vr-ink hover:text-vr-brand transition-colors";

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      <TopBar />
      <div className={`transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-xl shadow-md" : "bg-white"} border-b border-vr-border`}>
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[72px] flex items-center gap-6">
          <Link to="/" data-testid="logo-link" className="shrink-0">
            <img src="/vr-logo.jpg" alt="VR Packaging & Hygiene" className="h-10 md:h-11 w-auto" />
          </Link>

          {/* Search — between logo and cart */}
          <div className="hidden md:flex flex-1 max-w-2xl">
            <LiveSearch variant="compact" testIdPrefix="header-search" />
          </div>

          <nav className="hidden lg:flex items-center gap-6 ml-auto">
            <div onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)} className="relative">
              <button data-testid="nav-products" className={`${navLink} flex items-center gap-1`}>
                Products <ChevronDown size={15} />
              </button>
            </div>
            <Link to="/industries" data-testid="nav-industries" className={navLink}>Industries</Link>
            <Link to="/about" data-testid="nav-about" className={navLink}>About</Link>
            <Link to="/contact" data-testid="nav-contact" className={navLink}>Contact</Link>
            <Link to="/trade-account" data-testid="nav-trade" className="px-5 py-2.5 rounded-full bg-vr-navy text-white text-sm font-semibold hover:bg-vr-brand transition-colors">
              Trade Account
            </Link>
            <button data-testid="cart-open-btn" onClick={() => setCartOpen(true)} aria-label="Open quote list" className="relative grid place-items-center w-11 h-11 rounded-full border border-vr-border text-vr-ink hover:border-vr-brand hover:text-vr-brand transition-colors">
              <ShoppingBag size={18} />
              {count > 0 && <span data-testid="cart-count" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-vr-brand text-white text-[11px] font-bold flex items-center justify-center">{count}</span>}
            </button>
          </nav>

          <button data-testid="mobile-cart-btn" onClick={() => setCartOpen(true)} aria-label="Cart" className="lg:hidden ml-auto relative text-vr-ink mr-3">
            <ShoppingBag size={22} />
            {count > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-vr-brand text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </button>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-vr-ink" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {mega && (
            <motion.div
              ref={megaRef}
              onMouseEnter={() => setMega(true)}
              onMouseLeave={() => setMega(false)}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              data-testid="mega-menu"
              className="hidden lg:block absolute inset-x-0 bg-white border-b border-vr-border shadow-xl"
            >
              <div className="max-w-[1500px] mx-auto px-10 py-8 grid grid-cols-4 gap-8">
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-vr-brand font-bold mb-4">Food Service</div>
                  <ul className="space-y-2.5">
                    {fsSubs.map((s) => (
                      <li key={s.slug}>
                        <Link to={`/category/food-service?sub=${s.slug}`} onClick={() => setMega(false)} className="text-sm text-vr-dim hover:text-vr-brand transition-colors">
                          {s.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-span-3">
                  <div className="text-xs uppercase tracking-[0.15em] text-vr-brand font-bold mb-4">All Categories</div>
                  <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                    {categories.map((c) => (
                      <Link key={c.slug} to={`/category/${c.slug}`} onClick={() => setMega(false)} className="group flex items-center gap-3 rounded-lg p-2 hover:bg-vr-bg2 transition-colors">
                        <img src={c.image} alt="" className="w-10 h-10 rounded-md object-cover bg-vr-bg2" />
                        <div>
                          <div className="text-sm font-semibold text-vr-ink group-hover:text-vr-brand transition-colors">{c.name}</div>
                          <div className="text-xs text-vr-dim line-clamp-1">{c.tagline}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-white border-b border-vr-border">
            <div className="px-6 py-5 flex flex-col gap-4">
              <LiveSearch variant="compact" testIdPrefix="mobile-search" onNavigate={() => setOpen(false)} />
              <Link to="/products" onClick={() => setOpen(false)} className="font-display text-lg text-vr-ink">All Products</Link>
              <Link to="/category/food-service" onClick={() => setOpen(false)} className="font-display text-lg text-vr-ink">Food Service</Link>
              <Link to="/industries" onClick={() => setOpen(false)} className="font-display text-lg text-vr-ink">Industries</Link>
              <Link to="/about" onClick={() => setOpen(false)} className="font-display text-lg text-vr-ink">About</Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="font-display text-lg text-vr-ink">Contact</Link>
              <Link to="/trade-account" onClick={() => setOpen(false)} className="mt-1 px-5 py-3 rounded-full bg-vr-navy text-white font-semibold text-center">Open a Trade Account</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
